import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { useSearchParams, Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { SmartAlbumsCard } from "@/components/library/SmartAlbumsCard";
import { PasswordCard, PeopleCard } from "@/components/library/AccountCards";
import { MembersTab } from "@/components/admin/MembersTab";
import { StorageTab } from "@/components/admin/StorageTab";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const TABS = [["account", "Account"], ["library", "Library"], ["members", "Members", true], ["storage", "Storage", true]];
const OLD_TABS = { users: "members", people: "members", guests: "members" };

const ProfileCard = () => {
  const { user } = useAuth();
  return (
    <section className="settings-card" data-testid="profile-card">
      <h2 className="text-base font-semibold text-neutral-900">Profile</h2>
      <p className="text-sm text-neutral-700 mt-2" data-testid="profile-name">{user?.name}</p>
      <p className="text-sm text-neutral-500" data-testid="profile-email">{user?.email} · {user?.role === "admin" ? "Admin" : "Family"}</p>
    </section>
  );
};

const AiCard = () => {
  const [ai, setAi] = useState(null);
  useEffect(() => {
    const load = () => api.get("/settings/ai").then((r) => setAi(r.data));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  const toggle = async (on) => setAi((await api.post(`/settings/ai/${on ? "resume" : "pause"}`)).data);
  const pct = ai && ai.total ? ((ai.tagged + ai.skipped + ai.failed) / ai.total) * 100 : 0;
  return (
    <section className="settings-card" data-testid="ai-card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-neutral-900">AI tagging</h2>
          <p className="text-sm text-neutral-500">Tags photos in the background so you can search for “beach” or “birthday”.</p>
        </div>
        <Switch checked={ai ? !ai.paused : false} onCheckedChange={toggle} disabled={!ai} data-testid="settings-ai-tagging-toggle" />
      </div>
      <div className="mt-6" data-testid="settings-ai-tagging-progress">
        <Progress value={pct} className="h-2" />
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-neutral-600 mt-3">
          <span data-testid="ai-tagged-count">{ai?.tagged ?? "–"} tagged</span>
          <span data-testid="ai-remaining-count">{ai?.remaining ?? "–"} remaining</span>
          {ai?.failed ? <span data-testid="ai-failed-count">{ai.failed} failed</span> : null}
          <span className="text-neutral-400" data-testid="ai-status-label">{ai?.paused ? "Paused" : "Running"}</span>
        </div>
      </div>
    </section>
  );
};

const LibraryLinks = () => (
  <section className="settings-card" data-testid="photo-library-card">
    <h2 className="text-base font-semibold text-neutral-900">Photo Library (admin)</h2>
    <p className="text-sm text-neutral-500 mt-1">Every photo in one timeline for tidying: filters, bulk move/copy, add to album, delete.</p>
    <div className="flex flex-wrap gap-2 mt-4">
      <Link to="/library" className="btn-primary !py-1.5 !px-4 text-sm" data-testid="settings-open-photo-library">Open Photo Library</Link>
      <Link to="/albums/unsorted" className="btn-outline !py-1.5 !px-4 text-sm" data-testid="settings-open-unsorted">Unsorted</Link>
      <Link to="/duplicates" className="btn-outline !py-1.5 !px-4 text-sm" data-testid="settings-open-duplicates">Hidden duplicates</Link>
    </div>
  </section>
);

const TabBody = ({ tab, admin }) => {
  if (tab === "library") return <>{admin && <LibraryLinks />}<AiCard /><SmartAlbumsCard /></>;
  if (tab === "members") return <section className="settings-card"><MembersTab /></section>;
  if (tab === "storage") return <StorageTab />;
  return <>{!admin && <p className="text-sm text-neutral-600 mb-4 flex items-center gap-2" data-testid="account-private-note"><Lock size={14} /> Private photos are visible only to you.</p>}<ProfileCard /><PasswordCard />{!admin && <PeopleCard />}</>;
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const admin = user?.role === "admin";
  const tabs = TABS.filter(([, , adminOnly]) => admin || !adminOnly);
  const asked = OLD_TABS[params.get("tab")] || params.get("tab");
  const tab = tabs.some(([k]) => k === asked) ? asked : "account";
  return (
    <div className="settings-shell" data-testid="settings-page">
      <h1 className="serif text-4xl text-neutral-900 mb-6">Settings</h1>
      <div className="import-tabs settings-tabs" role="tablist" data-testid="settings-tabs">
        {tabs.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setParams({ tab: k })} data-testid={`settings-tab-${k}`}>{label}</button>
        ))}
      </div>
      <div className="settings-stack" data-testid={`settings-panel-${tab}`}><TabBody tab={tab} admin={admin} /></div>
    </div>
  );
}
